// Assuming you have these imports
import { auth } from "../../config/main_config";
import { getValidToken } from "./getValidToken.util";

// Global Rate Limit Manager to coordinate API requests across all services
interface PendingRequest {
    resolve: (value: any) => void;
    reject: (error: any) => void;
    priority: number;
    timestamp: number;
    requestFn: () => Promise<any>;
}

class RateLimitManager {
    private static instance: RateLimitManager;
    private queue: PendingRequest[] = [];
    private processing = false;
    private lastRateLimitTime = 0;
    private requestCount = 0;
    private requestWindowStart = 0;
    private readonly REQUESTS_PER_WINDOW = 30; // Very conservative: 30 requests per 10 seconds
    private readonly WINDOW_SIZE = 10000; // 10 seconds
    private readonly RATE_LIMIT_BUFFER = 10; // 10 requests buffer from the actual limit
    private readonly MAX_QUEUE_SIZE = 100;

    private constructor() {}

    static getInstance(): RateLimitManager {
        if (!RateLimitManager.instance) {
            RateLimitManager.instance = new RateLimitManager();
        }
        return RateLimitManager.instance;
    }

    async executeRequest<T>(requestFn: () => Promise<T>, priority: number = 0): Promise<T> {
        return new Promise((resolve, reject) => {
            // Check if queue is full
            if (this.queue.length >= this.MAX_QUEUE_SIZE) {
                reject(new Error('Rate limit queue is full'));
                return;
            }

            this.queue.push({
                resolve,
                reject,
                priority,
                timestamp: Date.now(),
                requestFn
            });

            // Sort by priority (higher priority first)
            this.queue.sort((a, b) => b.priority - a.priority);

            // If this is a high priority request and there are pending low priority requests,
            // process immediately
            if (priority >= 3 && this.queue.length > 0) {
                // Move high priority request to the front
                const highPriorityIndex = this.queue.findIndex(req => req.priority >= 3);
                if (highPriorityIndex > 0) {
                    const highPriorityReq = this.queue.splice(highPriorityIndex, 1)[0];
                    this.queue.unshift(highPriorityReq);
                }
            }

            this.processQueue();
        });
    }

    private async processQueue() {
        if (this.processing || this.queue.length === 0) {
            return;
        }

        this.processing = true;

        while (this.queue.length > 0) {
            const now = Date.now();
            
            // Reset window if needed
            if (now - this.requestWindowStart > this.WINDOW_SIZE) {
                this.requestCount = 0;
                this.requestWindowStart = now;
            }

            // Check if we're approaching the rate limit
            const availableSlots = this.REQUESTS_PER_WINDOW - this.requestCount;
            
            if (availableSlots <= this.RATE_LIMIT_BUFFER) {
                // Wait for the window to reset
                const waitTime = this.WINDOW_SIZE - (now - this.requestWindowStart);
                console.log(`[RateLimitManager] Approaching rate limit, waiting ${waitTime}ms`);
                await new Promise(resolve => setTimeout(resolve, waitTime));
                continue;
            }

            // If we were recently rate limited, add extra delay
            if (now - this.lastRateLimitTime < 5000) {
                const extraDelay = Math.min(5000 - (now - this.lastRateLimitTime), 2000);
                console.log(`[RateLimitManager] Recent rate limit detected, adding ${extraDelay}ms delay`);
                await new Promise(resolve => setTimeout(resolve, extraDelay));
            }

            // Process next request
            const pending = this.queue.shift();
            if (!pending) break;

            try {
                const result = await pending.requestFn();
                this.requestCount++;
                pending.resolve(result);
            } catch (error) {
                // Check if it's a rate limit error
                if (error instanceof Error && 
                    (error.message.includes('Status: 429') || error.message.includes('Too Many Requests'))) {
                    this.lastRateLimitTime = Date.now();
                    console.warn('[RateLimitManager] Rate limit hit, backing off');
                    
                    // Put the request back in the queue with delay
                    setTimeout(() => {
                        this.queue.unshift(pending);
                        this.processQueue();
                    }, 2000);
                    break;
                }
                pending.reject(error);
            }

            // Longer delay between requests to be conservative
            if (this.queue.length > 0) {
                await new Promise(resolve => setTimeout(resolve, 300));
            }
        }

        this.processing = false;
    }
}

// Create global instance
const rateLimitManager = RateLimitManager.getInstance();

/** API backend to use */
export type APIBackend = 'og' | 'thalassa';

interface FetchOptions {
    headers?: HeadersInit;
    body?: BodyInit | null | Record<string, any> | string | number | boolean | any[];
    companyID?: string;
    /** Which API backend to use: 'og' (default) or 'thalassa' */
    backend?: APIBackend;
    /** Priority for rate limiting (higher = more important) */
    priority?: number;
    // Include all other RequestInit properties
    method?: string;
    mode?: RequestMode;
    credentials?: RequestCredentials;
    cache?: RequestCache;
    redirect?: RequestRedirect;
    referrer?: string;
    referrerPolicy?: ReferrerPolicy;
    integrity?: string;
    keepalive?: boolean;
    signal?: AbortSignal;
    window?: null;
}

interface APIResponse<T = any> {
    data: T;
    status: number;
    statusText: string;
}

/** Get the base URL for the specified backend */
function getBaseURL(backend: APIBackend = 'og'): string {
    if (backend === 'thalassa') {
        // Support both VITE_THALASSA_API and VITE_THALASSA_URL for flexibility
        return (import.meta.env.VITE_THALASSA_API || import.meta.env.VITE_THALASSA_URL) as string;
    }
    // Default: original API
    return (window as any).apiURL ? (window as any).apiURL : (import.meta.env.VITE_apiURL as string);
}

/**
 * Validates that a company ID is a valid, non-empty string
 * Guards against race conditions where tenancy context hasn't loaded yet
 */
function isValidCompanyID(id: any): id is string {
    if (!id) return false;
    if (typeof id !== 'string') return false;
    const normalized = id.trim().toLowerCase();
    // Reject undefined, null, empty strings, and the literal string "undefined"
    return normalized !== '' && normalized !== 'undefined' && normalized !== 'null';
}

export const deiterateAPI = {
    request: async function <T = any>(
        method: string,
        url: string,
        options: FetchOptions = {},
        requireAuth: boolean = true,
        apiURL?: string
    ): Promise<T> {
        const baseURL = apiURL ?? getBaseURL(options.backend);

        // Get companyID from options, window, or localStorage (in priority order)
        // Filter out invalid values like undefined, null, empty string, "undefined"
        const rawCompanyID = options?.companyID || (window as any).companyID || localStorage.getItem('companyID');
        const companyID = isValidCompanyID(rawCompanyID) ? rawCompanyID : '';
        
        // Check if caller already provided company/customer/tenancy headers (case-insensitive)
        const existingHeaders = options.headers || {};
        const headerKeys = Object.keys(existingHeaders).map(k => k.toLowerCase());
        
        // Extract existing tenancy headers if they exist (and validate them)
        const rawExistingCompanyID = 
            existingHeaders['company-id'] || 
            existingHeaders['Company-ID'] || 
            existingHeaders['x-tenancy-id'] || 
            existingHeaders['X-Tenancy-Id'];
        const existingCompanyID = isValidCompanyID(rawExistingCompanyID) ? rawExistingCompanyID : undefined;
            
        const rawExistingCustomerID = 
            existingHeaders['customer-id'] || 
            existingHeaders['Customer-ID'];
        const existingCustomerID = isValidCompanyID(rawExistingCustomerID) ? rawExistingCustomerID : undefined;
            
        // Use existing headers if present, otherwise use computed companyID
        const finalCompanyID = existingCompanyID || companyID;
        const finalCustomerID = existingCustomerID || companyID;
        
        // Warn if no valid company ID is available (race condition detection)
        if (!finalCompanyID && options.backend === 'thalassa') {
            console.warn('[deiterateAPI] No valid companyID available for request to', url, '- tenancy may not be loaded yet');
        }
        
        // Build headers object, only including headers with valid values
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...existingHeaders, // Start with existing headers to preserve order
        };
        
        // Only add tenancy headers if they don't already exist and we have valid values
        if (finalCompanyID && !existingCompanyID) {
            headers['company-id'] = finalCompanyID;
            headers['x-tenancy-id'] = finalCompanyID;
        }
        
        if (finalCustomerID && !existingCustomerID) {
            headers['customer-id'] = finalCustomerID;
        }

        // Only try to get token if there's a current user
        // If requireAuth is true but no user, the request will fail on the backend
        if (auth.currentUser) {
            try {
                const idToken = await getValidToken(auth);
                if (idToken) {
                    let token = idToken.split('--__--')[0];
                    headers['Authorization'] = `Bearer ${token}`;
                } else {
                    console.warn('[deiterateAPI] getValidToken returned empty token');
                }
            } catch (error) {
                console.error('[deiterateAPI] Error getting ID token:', error);
                // If auth is required and we can't get a token, throw
                if (requireAuth) {
                    throw new Error('Authentication required but failed to get token');
                }
            }
        } else if (requireAuth) {
            console.warn('[deiterateAPI] No current user but auth required. Request may fail on backend. auth.currentUser:', auth.currentUser);
        }

        // console.log('Headers for API request:', headers);

        // Destructure to avoid options.headers overwriting our built headers
        const { headers: _ignoredHeaders, backend: _ignoredBackend, companyID: _ignoredCompanyID, priority = 0, ...restOptions } = options;

        // Auto-detect and handle JSON serialization for body
        let processedBody = restOptions.body;
        if (processedBody && typeof processedBody === 'object' && !(processedBody instanceof FormData) && !(processedBody instanceof Blob) && !(processedBody instanceof ArrayBuffer) && !(processedBody instanceof ReadableStream)) {
            // Check if it's already a string (already JSON.stringify'd)
            if (typeof processedBody === 'string') {
                // Try to parse it to see if it's valid JSON
                try {
                    JSON.parse(processedBody);
                    // If parsing succeeds, it's already stringified
                } catch {
                    // If parsing fails, it's a regular string that needs to be stringified
                    processedBody = JSON.stringify(processedBody);
                }
            } else {
                // It's an object that needs to be stringified
                processedBody = JSON.stringify(processedBody);
            }
        }

        const fetchOptions: RequestInit = {
            method,
            headers,
            // mode: 'no-cors',
            ...restOptions,
            body: processedBody as BodyInit,
        };

        // Create the actual fetch request function
        const makeRequest = async (): Promise<T> => {
            const response = await fetch(baseURL + url.replaceAll('/deiterateInternalAPI', ''), fetchOptions);
            const data: T = await response.json();

            if (!response.ok) {
                throw new Error(
                    `HTTP error! Status: ${response.status}, Message: ${response.statusText || data}`
                );
            }

            return data;
        };

        try {
            // Use RateLimitManager for Thalassa backend, direct fetch for OG backend
            if (options.backend === 'thalassa') {
                return await rateLimitManager.executeRequest(makeRequest, priority);
            } else {
                return await makeRequest();
            }
        } catch (error) {
            console.error('API request error:', error);
            throw error;
        }
    },

    POST<T = any>(url: string, options: FetchOptions = {}, ...args: any[]): Promise<T> {
        return deiterateAPI.request<T>('POST', url, options, ...args);
    },

    GET<T = any>(url: string, options: FetchOptions = {}, ...args: any[]): Promise<T> {
        return deiterateAPI.request<T>('GET', url, options, ...args);
    },

    PUT<T = any>(url: string, options: FetchOptions = {}, ...args: any[]): Promise<T> {
        return deiterateAPI.request<T>('PUT', url, options, ...args);
    },

    DELETE<T = any>(url: string, options: FetchOptions = {}, ...args: any[]): Promise<T> {
        return deiterateAPI.request<T>('DELETE', url, options, ...args);
    },

    PATCH<T = any>(url: string, options: FetchOptions = {}, ...args: any[]): Promise<T> {
        return deiterateAPI.request<T>('PATCH', url, options, ...args);
    },

    UPDATE<T = any>(url: string, options: FetchOptions = {}, ...args: any[]): Promise<T> {
        return deiterateAPI.request<T>('UPDATE', url, options, ...args);
    },

    /**
     * Create an authenticated EventSource for Server-Sent Events (SSE)
     * EventSource doesn't support custom headers, so we append auth as query params
     */
    async createEventSource(url: string, options: FetchOptions = {}): Promise<EventSource> {
        const baseURL = getBaseURL(options.backend);
        
        // Get auth token - use raw JWT for query param
        const token = await getValidToken(auth, { rawIdTokenOnly: true });
        if (!token) {
            throw new Error('No valid authentication token available');
        }

        // Get companyID
        const rawCompanyID = options?.companyID || (window as any).companyID || localStorage.getItem('companyID');
        const companyID = isValidCompanyID(rawCompanyID) ? rawCompanyID : '';

        if (!companyID) {
            throw new Error('No valid company ID available for SSE connection');
        }

        // Build URL with auth params (EventSource doesn't support custom headers)
        const urlObj = new URL(baseURL + url);
        urlObj.searchParams.set('token', token);
        urlObj.searchParams.set('companyID', companyID);

        const eventSource = new EventSource(urlObj.toString(), {
            withCredentials: true,
        });

        return eventSource;
    },

    // Additional methods can be added here if needed
};

// Export the rate limit manager for external access
export { RateLimitManager, rateLimitManager };
